import { isEmpty } from 'lodash-es';
import { PropsWithChildren, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  IBulkChartsPayload,
  IComposableAnalyticsCharts,
  IComposableChartsWidget,
  IComposableDashboard,
  IComposableDashboardContext,
  IPostgreAnalyticsBulkPayload,
  ISubmissionAnalyticsPayload,
  fetchMyDashboards,
  fetchPostgreBulkChartsResponse,
} from 'wisestep-api';

import { useWorkspaceContext } from '../../../context';
import { fetchZoomAccountOwner } from '../../composableAnalytics/BaseCharts/helper/getFrequencyForAppliedDateRange';
import { Index } from '../../composableAnalytics/Widgets/helper/freeTextWidgetKeys';
import { getDefaultDashboardValues } from '../helper/getDefaultDashboardValues';
import { IReactGridLayout } from '../types';

export const ComposableDashboardContext = createContext<IComposableDashboardContext | null>(null);

export default function ComposableDashboardContextProvider(
  props: PropsWithChildren<IComposableDashboardContext | null>
) {
  const [dashboards, setDashboards] = useState<IComposableDashboard[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [bulkChartsPayload, setBulkChartsPayload] = useState<IBulkChartsPayload[]>([]);
  const [postgreBulkPayload, setPostgreBulkPayload] = useState<IPostgreAnalyticsBulkPayload>({
    filters: [],
    charts: [],
  });
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchChartsInBulk = useRef<boolean>(true);
  const [bulkChartsResponse, setBulkChartsResponse] = useState<Record<string, object> | null>(null);
  const [postgreBulkChartsResponse, setPostgreBulkChartsResponse] = useState<Record<
    string,
    object
  > | null>(null);
  const workspace = useWorkspaceContext();
  const [selectedDashboard, setSelectedDashboard] =
    useState<IComposableDashboard>(getDefaultDashboardValues);
  const isMetricChart = (key: string) => key.split('_')[0] === 'metric';
  const isTableChart = (key: string) => key.split('_')[0] === 'table';
  const isBarChart = (key: string) => key.split('_')[0] === 'barchart';
  const isStackedBarChart = (key: string) => key.split('_')[0] === 'stackedbarchart';
  const isChartWithFilter = (key: string) => key.includes('withFilter');
  const splitMetrics = ['cvupload', 'pipeline', 'jobs'];
  const isSplitMetric = (key: string) => splitMetrics.some((value) => key.includes(value));
  const complexTables = ['cvupload'];
  const isComplexTable = (key: string) => complexTables.some((value) => key.includes(value));
  const generateLayout = (chartObj: IComposableChartsWidget, index: number) => {
    const key = chartObj.type.toLowerCase();
    const category = chartObj.category;
    let layout: Partial<IReactGridLayout> = {
      x: (index * 6) % 12,
      y: Math.floor((index * 6) / 12),
      w: 6,
      h: 4,
      minW: 3,
      minH: 4,
      maxH: undefined,
    };
    if (key == Index.FREE_TEXT_WIDGET) {
      layout = {
        ...layout,
        w: 8,
        h: 0.9,
        minH: 0.5,
        minW: 7,
      };
    } else if (isMetricChart(key)) {
      layout = {
        ...layout,
        x: (index * 2) % 12,
        y: Math.floor((index * 2) / 12),
        w: 2,
        h: 1,
        minW: 2,
        minH: 1,
      };
      if (key.includes('split') && isSplitMetric(category))
        layout = { ...layout, w: 12, h: 3, minW: 12, minH: 3, maxH: 3 };
    } else {
      const fiveRowTablePattern =
        /(source_mappings|service|emails|sms|table_lead|table_rtr|submissions_recruiter_stages|table_followups|recruiter_mappings|calls_recruiter|profile_download_source|recruiter_team|job_title_mappings|recruiter_download_source|recruiter_source|revenue|jobs_by_submission|wjd_candidate_wise_stage|clientnotes|clientspocnotes|jobs_account_manager|recruiter_actions|candidate_response|jobspostedspocs|jobs_type_spoc|jobs_client_and_client_spoc|jobs_status_spoc|users_mappings|table_users_mappings|mappings_sent_rtr|table_clients_mappings|table_mappings_by_rec|am_submissions|submissions_by_am|submissions_by_rec|submissions_job_type|submissions_by_job_type|submissions_by_client_and_client_spoc|submissions_client_and_client_spoc|am_submissions|submissions_am|businessDefinition_account_manager|businessdefinition_recruiter|users_submissions|table_clients_submissions|table_client_wise|submisions_by_stages|am_mappings|mappings_am|mappings_by_am|mappings_senty_rtr|businessdefinition_account_manager|jobsposted|jobs_submission_count|jobs_status_client|jobs_type_client|businessdefinition_client_wise_submission|businessdefinition_submission_client_spoc|businessdefinition_client_split|businessdefinition_candidate_wise_stage|businessdefinition_stage_state|submissions_joining_date)/;
      const threeRowTablesPattern =
        /(download_submissions_source|submissions_by_download_source|jobs_worked|submissions_by_source|source_submissions|mappings_by_source|job_type|jobtype|no_mappings|no_submissions|submissions_source|table_mappings_source|table_jobs_no_subissions|businessdefinition_submission_job_title)/;
      if (isTableChart(key)) layout = { ...layout, h: 4.7, minH: 4.7, maxH: 4.7 };
      if (isBarChart(key) || isStackedBarChart(key)) layout = { ...layout, h: 3, minH: 3 };
      if (isComplexTable(category)) layout = { ...layout, w: 8, minW: 8 };
      if (isChartWithFilter(key)) layout = { ...layout, h: 5.3, minH: 5.3, maxH: 5.3 };
      if (
        isTableChart(key) &&
        fiveRowTablePattern.test(key) &&
        [
          'submissions',
          'mappings',
          'jobs',
          'businessDefinition',
          'client_notes',
          'revenue',
          'calls',
          'followUps',
          'rtr',
          'lead',
          'sms',
          'emails',
        ].includes(category)
      )
        layout = { ...layout, h: 2.9, minH: 2.9, maxH: 2.9 };
      if (isTableChart(key) && threeRowTablesPattern.test(key))
        layout = { ...layout, h: 2.3, minH: 2.3, maxH: 2.3 };
    }
    return layout;
  };

  const addWidget = (chartObj: IComposableChartsWidget) => {
    setSelectedDashboard((prevSelectedDashboard) => {
      const prevCharts = prevSelectedDashboard.charts as IComposableAnalyticsCharts[];
      const newChart: IComposableAnalyticsCharts = {
        id: uuidv4(),
        name: chartObj.name,
        filters: {},
        type: chartObj.type,
        layout: generateLayout(chartObj, prevCharts.length),
        text: chartObj.text,
      };
      prevCharts.push(newChart);
      return { ...prevSelectedDashboard };
    });
  };

  const removeWidget = (chartObj: IComposableChartsWidget) => {
    setSelectedDashboard((prevSelectedDashboard) => {
      const prevCharts = prevSelectedDashboard.charts as IComposableAnalyticsCharts[];
      const index = prevCharts.findIndex((chart) => chart.type === chartObj.type);
      prevCharts.splice(index, 1);
      return { ...prevSelectedDashboard };
    });
  };

  const updateTableWidget = (chartType: string, newSetting: Record<string, any>) => {
    setSelectedDashboard((prevSelectedDashboard) => {
      const tableWidget = (prevSelectedDashboard.charts as IComposableAnalyticsCharts[]).find(
        (chart) => chart.type === chartType
      );
      tableWidget && (tableWidget.setting = { ...tableWidget.setting, ...newSetting });
      return { ...prevSelectedDashboard };
    });
  };

  const setSelectedDashboardByIndex = (index: number) => {
    if (index !== -1) {
      const charts: IComposableAnalyticsCharts[] =
        dashboards[index]?.charts.length > 0 ? JSON.parse(dashboards[index]?.charts as string) : [];
      setSelectedDashboard({
        ...dashboards[index],
        charts,
      });
      if (charts.length > 0) fetchChartsInBulk.current = true;
    } else {
      const defaultValues = getDefaultDashboardValues();
      defaultValues.workspaceId = workspace?.id ?? '';
      defaultValues.createdBy = workspace.userId ?? '';
      setSelectedDashboard(defaultValues);
      fetchChartsInBulk.current = false;
    }
  };

  const pushChartPayloadForBulkFetch = useCallback((id: string, chartPayload: any, api: string) => {
    const bulkPayload: IBulkChartsPayload = {
      id,
      chartPayloadAsJsonString: JSON.stringify(chartPayload),
      api,
    };
    setBulkChartsPayload((p) => {
      const duplicateExistIndex = p.findIndex((payload) => payload.id === id);
      if (duplicateExistIndex !== -1) {
        p[duplicateExistIndex] = bulkPayload;
        return [...p];
      } else return [...p, bulkPayload];
    });
  }, []);

  const pushPostgrePayloadForBulkFetch = useCallback(
    (id: string, chartPayload: ISubmissionAnalyticsPayload, restructuredFilters: any[]) => {
      let pipelineData = restructuredFilters.find(
        (filter) => filter.filterName == 'candidateStageStatusPipelineData'
      );
      pipelineData = pipelineData
        ? { [pipelineData?.filterName]: pipelineData?.values[0] ? pipelineData?.values[0] : '' }
        : {};
      setPostgreBulkPayload((p) => {
        p?.charts.push({ id, ...chartPayload });
        const newBulkPayload = {
          filters: restructuredFilters.filter(
            (filter) => filter.filterName != 'candidateStageStatusPipelineData'
          ),
          charts: p?.charts ?? [],
          ...pipelineData,
        };
        return newBulkPayload;
      });
    },
    []
  );

  useEffect(() => {
    const fetchAllDashboards = async () => {
      setLoader(true);
      try {
        const response = await fetchMyDashboards();
        const userDashboards: IComposableDashboard[] = response.data;
        userDashboards.forEach((userDashboard) => {
          const charts = JSON.parse(userDashboard.charts as string) as IComposableAnalyticsCharts[];
          charts.forEach((chart) => !chart.id && (chart.id = uuidv4()));
          userDashboard.charts = JSON.stringify(charts);
        });
        setDashboards(userDashboards);
        setLoader(false);
        const zoomAccountOwnerId = localStorage.getItem('zoomAccountOwnerId');
        if (zoomAccountOwnerId === null) {
          fetchZoomAccountOwner();
        }
      } catch (e) {
        console.log(e);
        setLoader(false);
      }
    };
    fetchAllDashboards();
  }, []);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(async () => {
      if (fetchChartsInBulk.current) {
        try {
          const response = await fetchPostgreBulkChartsResponse(postgreBulkPayload);
          setPostgreBulkChartsResponse(response.data);
        } catch (error) {
          console.log('error fetching data from bulk-with-filters', error);
        }
      }
    }, 200);
  }, [postgreBulkPayload]);

  const [isReadFromResponseMap, setReadFromResponseMap] = useState<string[]>([]);
  const [isReadFromPostgreResponseMap, setReadFromPostgreResponseMap] = useState<string[]>([]);
  const getChartResponseFromBulk = useCallback(
    (id: string) => {
      if (bulkChartsResponse !== null) setReadFromResponseMap((r) => [...r, id]);
      if (fetchChartsInBulk.current && !isEmpty(bulkChartsResponse)) {
        if (typeof bulkChartsResponse?.[id] == 'string')
          return JSON.parse((bulkChartsResponse?.[id] as object).toString());
        else return bulkChartsResponse?.[id];
      } else return {};
    },
    [bulkChartsResponse]
  );

  const getPostgreChartResponseFromBulk = useCallback(
    (id: string) => {
      if (postgreBulkChartsResponse !== null) {
        setReadFromPostgreResponseMap((r) => [...r, id]);
      }
      return fetchChartsInBulk.current && !isEmpty(postgreBulkChartsResponse)
        ? postgreBulkChartsResponse?.[id]
        : null;
    },
    [postgreBulkChartsResponse]
  );

  useEffect(() => {
    if (
      isReadFromResponseMap.length == Object.keys(bulkChartsPayload).length &&
      isReadFromPostgreResponseMap.length == postgreBulkPayload.charts?.length &&
      fetchChartsInBulk.current
    ) {
      fetchChartsInBulk.current = false;
      setBulkChartsPayload([]);
      setPostgreBulkPayload({
        filters: [],
        charts: [],
      });
      setBulkChartsResponse(null);
      setPostgreBulkChartsResponse(null);
      isReadFromResponseMap.length = 0;
      isReadFromPostgreResponseMap.length = 0;
    }
  }, [
    bulkChartsPayload,
    isReadFromPostgreResponseMap,
    isReadFromResponseMap,
    postgreBulkPayload.charts?.length,
  ]);

  return (
    <ComposableDashboardContext.Provider
      value={{
        dashboards,
        loader,
        setLoader,
        setDashboards,
        selectedDashboard,
        setSelectedDashboard,
        updateTableWidget,
        addWidget,
        removeWidget,
        setSelectedDashboardByIndex,
        pushChartPayloadForBulkFetch,
        pushPostgrePayloadForBulkFetch,
        fetchChartsInBulk,
        getChartResponseFromBulk,
        getPostgreChartResponseFromBulk,
        setPostgreBulkPayload,
      }}
    >
      {props.children}
    </ComposableDashboardContext.Provider>
  );
}
